import React from "react";
import styled from "styled-components";
import Box from "react-raster";
import {
  Paper,
  Heading,
  Text,
  InputForm,
  Spacer,
  Radio,
  Accordion,
  AccordionItem,
  AccordionContent,
  AccordionTrigger
} from "src/components";
import { AuthStore, RoutingStore, SpaceStore, UserStore } from "src/stores";
import { useTranslation } from "react-i18next";
import { inject, observer } from "mobx-react";
import { theme } from "src/config";
import { SideMenu } from "src/components/sideMenu";
import { NewsFeedSettings, SpaceType } from "src/types";
import { Space } from "src/models";

const AccordionContainer = styled.div`
margin-left: 0.5rem;
`

export interface INotificationsSettingsProps {
  authStore?: AuthStore;
  routingStore?: RoutingStore;
  userStore?: UserStore;
  spaceStore?: SpaceStore;
}

export const NewsfeedSettings = inject(
  "routingStore",
  "authStore",
  "userStore",
  "spaceStore"
)(
  observer((props: INotificationsSettingsProps): JSX.Element => {
    const { t, i18n } = useTranslation();
    const user = props.authStore.getCurUser;
    const { spaceStore, authStore, userStore } = props;


    const options: {
      label?: string;
      setting?: NewsFeedSettings;
      color?: string;
    }[] = [
      {
        label: t("no_newsfeed_notifications"),
        setting: NewsFeedSettings.None,
        color: "red"
      },
      {
        label: t("affected_newsfeed_notifications"),
        setting: NewsFeedSettings.Affected,
        color: "blue"
      },
      {
        label: t("all_newsfeed_notifications"),
        setting: NewsFeedSettings.All,
        color: "yellow"
      }
    ];

    const projects: Space[] = spaceStore.getUserSpaces(SpaceType.Project);
    const roomsByProject: { [project_id: number]: Space[] } = projects.reduce(
      (res: { [project_id: number]: Space[] }, val) => {
        const rooms = spaceStore.getUserItemsByParent(val.id, SpaceType.Room);
        if (true || (rooms && rooms.length > 0)) {
          res[val.id] = rooms;
        }
        return res;
      },
      {}
    );

    if (!user) {
      return <></>;
    }

    return (
      <Paper
        display        = "grid"
        justifyContent = "center"
        colspan        = {10}
        flexDirection  = "row"
        style          = {theme.paper.regular}
        $center
        $noStyling
      >
        <Box display="flex" cols={1}>
          <SideMenu />
        </Box>

        <Box marginLeft="1rem" display="flex" cols={9}>
          <Paper $noShadow width="100%" onChange={user.saveSpaceSettings}>
            <Box gridRowGap={theme.layout.gutter} alignItems="left">
              <Box>
                <Heading type="h3">{t("newsfeed_settings")}</Heading>
              </Box>
              <InputForm>
                {projects.map((p) => (
                  <React.Fragment key={p.id}>
                    <RowTitle>
                      <Text size="large" weight="bold">
                        {p.title}
                      </Text>
                    </RowTitle>
                    {roomsByProject[p.id].length > 0 ? (
                      <AccordionContainer key={p.id}>
                      <Accordion type="single" defaultValue={String(authStore.getCurRoomID||roomsByProject[p.id][0].id)} collapsible>
                        {roomsByProject[p.id]?.map((r) => (
                          <AccordionItem value={r.id.toString()} key={r.id}>
                            <AccordionTrigger
                              rightElement={
                                <SettingBadge
                                  color={
                                    user.spaces[r.id]?.news_feed ===
                                    NewsFeedSettings.Affected
                                      ? `${theme.color.green8}`
                                      : user.spaces[r.id]?.news_feed ===
                                        NewsFeedSettings.None
                                      ? `${theme.color.slate9}`
                                      : user.spaces[r.id]?.news_feed ===
                                        NewsFeedSettings.All
                                      ? ` ${theme.color.blue9}`
                                      : undefined
                                  }
                                >
                                  {t(user.spaces[r.id]?.news_feed)}
                                </SettingBadge>
                              }
                            >
                              <Text size="small">{r.title}</Text>
                            </AccordionTrigger>
                            <AccordionContent>
                              {options.map((o) => (
                                <Radio
                                  key     = {o.label}
                                  value   = {t(o.label)}
                                  checked = {
                                    user.spaces[r.id].news_feed === o.setting
                                  }
                                  onClick={() =>
                                    user.setSpaceSetting(
                                      r.id,
                                      "news_feed",
                                      o.setting,
                                      true
                                    )
                                  }
                                />
                              ))}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                      </AccordionContainer>
                    ) : (
                      <Text color={theme.color.slate11} size="small">
                        {t("no_room_yet")}
                      </Text>
                    )}
                  </React.Fragment>
                ))}
              </InputForm>
            </Box>
          </Paper>
        </Box>
      </Paper>
    );
  })
);



const SettingBadge = styled.div<{ color?: string }>`
  background-color: ${(p) => (p.color ? p.color : undefined)};
  font-weight:      ${theme.font.weight.bold};
  color:            white;
  border-radius:    0.4rem;
  padding:          0.5rem;
  margin-right:     2rem;
  font-size:        ${theme.font.size.xsmall};
`;

const RowTitle = styled.div`
  background-color:  ${theme.color.grayLightest};
  padding: 1rem 0.5rem;
  margin-top: 1rem;
  text-transform: uppercase;
`
